<script lang="ts" setup>
import { Size } from '@/types/common.types';
import { IconType } from '@/types/icon.types';
import { computed, InputHTMLAttributes, InputTypeHTMLAttribute, ref, useSlots } from 'vue';

interface Props {
  value: InputHTMLAttributes['value'];
  type?: InputTypeHTMLAttribute;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  readonly?: boolean;
  error?: boolean;
  active?: boolean;
  className?: string | string[];
  size?: Size;
  clearable?: boolean;
  clearIcon?: IconType;
  clickableIcon?: boolean;
  prefixIcon?: IconType;
  suffixIcon?: IconType;
  prefixInnerIcon?: IconType;
  suffixInnerIcon?: IconType;
  hintMessage?: string;
  validationMessage?: string;
  rules?: (value: InputHTMLAttributes['value']) => boolean;
}

interface Emit {
  (e: 'on-focus', event: FocusEvent, value: InputHTMLAttributes['value']): void;
  (e: 'on-blur', event: FocusEvent, value: InputHTMLAttributes['value']): void;
  (e: 'on-change', event: Event, value: InputHTMLAttributes['value']): void;
  (e: 'on-clear', event: MouseEvent, value: InputHTMLAttributes['value']): void;
  (
    e: 'on-prefix-icon-click',
    event: MouseEvent,
    value: {
      value: InputHTMLAttributes['value'];
      icon?: IconType;
    },
  ): void;
  (
    e: 'on-prefix-inner-icon-click',
    event: MouseEvent,
    value: {
      value: InputHTMLAttributes['value'];
      icon?: IconType;
    },
  ): void;
  (
    e: 'on-suffix-icon-click',
    event: MouseEvent,
    value: {
      value: InputHTMLAttributes['value'];
      icon?: IconType;
    },
  ): void;
  (
    e: 'on-suffix-inner-icon-click',
    event: MouseEvent,
    value: {
      value: InputHTMLAttributes['value'];
      icon?: IconType;
    },
  ): void;
}

const props = withDefaults(defineProps<Props>(), {
  value: '',
  type: 'text',
  label: '',
  placeholder: '',
  disabled: false,
  readonly: false,
  error: false,
  active: false,
  className: '',
  size: 'medium',
  clearable: false,
  clearIcon: undefined,
  clickableIcon: false,
  prefixIcon: undefined,
  suffixIcon: undefined,
  prefixInnerIcon: undefined,
  suffixInnerIcon: undefined,
  hintMessage: '',
  validationMessage: '',
  rules: undefined,
});

const emit = defineEmits<Emit>();
const modelValue = defineModel<InputHTMLAttributes['value']>('value', { required: true });

const slots = useSlots();
const touched = ref<boolean>(false);
const inputRef = ref<HTMLInputElement | null>(null);

// focus 메서드 노출
const focus = () => {
  inputRef.value?.focus();
};

defineExpose({
  focus,
});

const onInputFocus = (event: FocusEvent) => {
  emit('on-focus', event, modelValue.value);
};

const onInputBlur = (event: FocusEvent) => {
  touched.value = true;
  emit('on-blur', event, modelValue.value);
};

const onInputChange = (event: Event) => {
  touched.value = true;
  emit('on-change', event, modelValue.value);
};

const onPrefixIconClick = (event: MouseEvent) => {
  emit('on-prefix-icon-click', event, { value: modelValue.value, icon: props.prefixIcon });
};

const onPrefixInnerIconClick = (event: MouseEvent) => {
  emit('on-prefix-inner-icon-click', event, {
    value: modelValue.value,
    icon: props.prefixInnerIcon,
  });
};

const onSuffixIconClick = (event: MouseEvent) => {
  emit('on-suffix-icon-click', event, { value: modelValue.value, icon: props.suffixIcon });
};

const onSuffixInnerIconClick = (event: MouseEvent) => {
  emit('on-suffix-inner-icon-click', event, {
    value: modelValue.value,
    icon: props.suffixInnerIcon,
  });
};

const onClear = (event: MouseEvent) => {
  modelValue.value = '';
  emit('on-clear', event, modelValue.value);
};

const isValidationError = computed(() => {
  if (!touched.value && props.rules) return false;
  return props.rules ? props.rules(modelValue.value) : props.validationMessage;
});

const inputClass = computed(() => {
  return [
    'base-input__input',
    { 'base-input__input--active': props.active },
    { 'base-input__input--error': props.error || isValidationError.value },
    { 'base-input__input--icon-left': props.prefixInnerIcon },
    { 'base-input__input--icon-right': props.suffixInnerIcon },
    props.className.length > 0 ? [...props.className] : props.className,
    props.size,
  ];
});

const isClickableIcon = computed(() => {
  return (
    props.clickableIcon &&
    (props.prefixIcon || props.prefixInnerIcon || props.suffixIcon || props.suffixInnerIcon)
  );
});

const BASE_ICON_CLASS = 'base-input__icon';

const prefixIconClass = computed(() => {
  return [
    BASE_ICON_CLASS,
    'base-input__icon-prefix',
    props.prefixIcon,
    { 'base-input__icon--clickable': isClickableIcon.value },
  ];
});
const prefixInnerIconClass = computed(() => {
  return [
    BASE_ICON_CLASS,
    'base-input__icon-prefix-inner',
    props.prefixInnerIcon,
    { 'base-input__icon--clickable': isClickableIcon.value },
  ];
});
const suffixIconClass = computed(() => {
  return [
    BASE_ICON_CLASS,
    'base-input__icon-suffix',
    props.suffixIcon,
    { 'base-input__icon--clickable': isClickableIcon.value },
  ];
});
const suffixInnerIconClass = computed(() => {
  return [
    BASE_ICON_CLASS,
    'base-input__icon-suffix-inner',
    props.suffixInnerIcon,
    { 'base-input__icon--clickable': isClickableIcon.value },
  ];
});

const rightInnerIcons = computed(() => {
  const icons: Array<'suffixInner' | 'clear'> = [];
  if (props.suffixInnerIcon) icons.push('suffixInner');
  if (props.clearable && modelValue.value) icons.push('clear');
  return icons;
});

const rightIconCount = computed(() => rightInnerIcons.value.length);

const inputPaddingStyle = computed(() => {
  const leftBase = 8;
  const left = props.prefixInnerIcon ? leftBase + 24 : leftBase;

  const rightBase = 8;
  const right = rightBase + rightIconCount.value * 24;

  return {
    paddingLeft: `${left}px`,
    paddingRight: `${right}px`,
  } as Record<string, string>;
});
</script>

<template>
  <div class="base-input-wrapper">
    <div
      v-if="slots.prepend"
      class="base-input__prepend"
    >
      <slot name="prepend" />
    </div>
    <div
      v-if="props.prefixIcon"
      class="base-input__icon-wrapper base-input__icon-wrapper-prefix"
    >
      <span
        :class="prefixIconClass"
        @click="onPrefixIconClick"
      />
    </div>
    <div class="base-input">
      <div
        v-if="props.label"
        class="base-input__label"
      >
        {{ props.label }}
      </div>
      <div
        v-if="slots.prependInner"
        class="base-input__prepend-inner"
      >
        <slot name="prependInner" />
      </div>
      <div
        v-if="props.prefixInnerIcon"
        class="base-input__icon-wrapper base-input__icon-wrapper-prefix-inner"
      >
        <span
          :class="prefixInnerIconClass"
          @click="onPrefixInnerIconClick"
        />
      </div>
      <input
        ref="inputRef"
        v-model="modelValue"
        :placeholder="placeholder"
        :type="type"
        :class="inputClass"
        :disabled="disabled"
        :readonly="readonly"
        :style="inputPaddingStyle"
        @focus="onInputFocus"
        @blur="onInputBlur"
        @input="onInputChange"
      />
      <div
        v-if="rightInnerIcons.length"
        class="base-input__right-inner-icons"
        :style="{ right: '8px' }"
      >
        <template
          v-for="icon in rightInnerIcons"
          :key="icon"
        >
          <span
            v-if="icon === 'suffixInner'"
            :class="suffixInnerIconClass"
            @click="onSuffixInnerIconClick"
          />
          <span
            v-else-if="icon === 'clear'"
            :class="[props.clearIcon || 'ev-icon-s-close', 'base-input__icon--clickable']"
            @click="onClear"
          />
        </template>
      </div>
      <div
        v-if="slots.appendInner"
        class="base-input__append-inner"
      >
        <slot name="appendInner" />
      </div>
    </div>
    <div
      v-if="props.suffixIcon"
      class="base-input__icon-wrapper base-input__icon-wrapper-suffix"
    >
      <span
        :class="suffixIconClass"
        @click="onSuffixIconClick"
      />
    </div>
    <div
      v-if="slots.append"
      class="base-input__append"
    >
      <slot name="append" />
    </div>
    <div
      v-if="props.hintMessage"
      class="base-input__hint-message"
    >
      {{ props.hintMessage }}
    </div>
    <div
      v-if="isValidationError"
      class="base-input__validation-message"
    >
      {{ props.validationMessage }}
    </div>
  </div>
</template>

<style lang="scss" scoped>
.base-input-wrapper {
  display: inline-flex;
  position: relative;
  align-items: center;
  width: 100%;
}
.base-input {
  position: relative;
  flex: 1 1 0;
  width: 100%;
}
.base-input__input {
  position: relative;
  z-index: 0;
  width: 100%;
  height: 28px;
  padding: 6px 10px 6px 8px;
  border: 1px solid var(--input-text-border-color);
  border-radius: 2px;
  outline: transparent;
  background-color: var(--input-text-bg-color);
  font-size: 13px;
  color: var(--main-font-color);
  transition: all 0.3s;

  &:hover {
    border-color: var(--input-text-hover-border-color);
  }

  &:focus {
    border-color: var(--input-text-active-border-color);
  }

  &:read-only {
    cursor: default;
  }

  &:disabled {
    cursor: not-allowed;
    background-color: var(--input-text-disabled-bg-color);
    color: var(--input-text-disabled-font-color);
  }

  &--active {
    border-color: var(--input-text-active-border-color);

    &:focus,
    &:hover {
      border-color: var(--input-text-active-border-color);
    }
  }

  &--error {
    border-color: var(--input-text-error-border-color);

    &:focus,
    &:hover {
      border-color: var(--input-text-error-border-color);
    }
  }

  &--icon-left {
    padding-left: 32px;
  }

  &--icon-right {
    padding-right: 32px;
  }

  &.xsmall {
    height: 20px;
    font-size: 12px;
  }

  &.small {
    height: 24px;
    font-size: 12px;
  }

  &.medium {
    height: 28px;
    font-size: 13px;
  }

  &.large {
    height: 36px;
    font-size: 14px;
  }

  &.xlarge {
    height: 36px;
    font-size: 14px;
  }
}
.base-input__icon-wrapper {
  display: inline-flex;
  align-items: center;

  &-prefix {
    margin-right: 10px;
  }
  &-suffix {
    margin-left: 10px;
  }
  &-prefix-inner {
    left: 8px;
  }
  &-suffix-inner {
    right: 8px;
    margin-right: 10px;
  }
  &-clear {
    right: 8px;
  }
}
.base-input__icon-wrapper-prefix-inner,
.base-input__icon-wrapper-suffix-inner,
.base-input__icon-wrapper-clear {
  display: inline-flex;
  position: absolute;
  top: 50%;
  z-index: 1;
  transform: translateY(-50%);
}
.base-input__icon {
  width: 16px;
  height: 16px;
  pointer-events: none;
  font-size: 16px;

  &--clickable {
    cursor: pointer;
    pointer-events: auto;
  }

  &::placeholder {
    padding-left: 8px;
  }
}
.base-input__icon[class^='icon-'],
.base-input__icon[class*=' icon-'] {
  background-color: var(--icon-base-color);
  mask-size: 16px;
}
.base-input__right-inner-icons {
  display: flex;
  position: absolute;
  top: 50%;
  gap: 8px;
  align-items: center;
  transform: translateY(-50%);
}
.base-input__label {
  display: inline-flex;
  position: absolute;
  top: -15px;
  left: 0;
  font-size: 12px;
}
.base-input__hint-message {
  display: inline-flex;
  position: absolute;
  top: 99%;
  left: 0;
  align-items: center;
  height: 13px;
  font-size: 12px;
  color: var(--input-text-placeholder-color);
}
.base-input__validation-message {
  display: inline-flex;
  position: absolute;
  top: 99%;
  left: 0;
  align-items: center;
  height: 13px;
  font-size: 12px;
  color: var(--input-text-error-font-color);

  &::before {
    display: inline-block;
    width: 12px;
    height: 12px;
    margin-right: 2px;
    background-color: var(--input-text-error-font-color);
    content: '';
    -webkit-mask: url('@/common/assets/sparrow/icon-sparrow-fill-error-circle.svg') center / contain
      no-repeat;
    mask: url('@/common/assets/sparrow/icon-sparrow-fill-error-circle.svg') center / contain
      no-repeat;
  }
}
</style>
